import React from 'react';
import {
  Paper,
  Typography,
  Box,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  Chip,
  LinearProgress
} from '@mui/material';
import LocationOnIcon from '@mui/icons-material/LocationOn';

interface GeographicData {
  city: string;
  region: string;
  count: number;
}

interface GeographicDistributionProps {
  data: GeographicData[];
  isLoading: boolean;
}

const GeographicDistribution: React.FC<GeographicDistributionProps> = ({ data, isLoading }) => {
  if (isLoading) {
    return (
      <Paper sx={{ p: 3, textAlign: 'center' }}>
        <CircularProgress />
        <Typography sx={{ mt: 2, color: '#fff' }}>Loading geographic data...</Typography>
      </Paper>
    );
  }

  if (!data || data.length === 0) {
    return (
      <Paper sx={{ p: 3 }}>
        <Typography sx={{ color: '#fff' }}>No geographic data available</Typography>
      </Paper>
    );
  }

  const maxCount = Math.max(...data.map(item => item.count));

  return (
    <Paper sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <LocationOnIcon sx={{ color: '#fff', mr: 1 }} />
        <Typography variant="h6" sx={{ color: '#fff' }}>
          Geographic Distribution
        </Typography>
      </Box>

      <List sx={{ maxHeight: 600, overflow: 'auto' }}>
        {data.map((location, index) => (
          <ListItem key={index} sx={{ px: 0, py: 1 }}>
            <Box sx={{ width: '100%' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <ListItemText
                  primary={
                    <Typography sx={{ color: '#fff', fontWeight: 'bold', fontSize: '0.875rem' }}>
                      {location.city !== 'Unknown' ? location.city : 'Unknown City'}
                    </Typography>
                  }
                  secondary={
                    <Typography sx={{ color: '#bbb', fontSize: '0.75rem' }}>
                      {location.region !== 'Unknown' ? location.region : 'Unknown Region'}
                    </Typography>
                  }
                />
                <Chip
                  label={`${location.count} client${location.count !== 1 ? 's' : ''}`}
                  size="small"
                  sx={{
                    backgroundColor: '#1976d2',
                    color: '#fff',
                    fontWeight: 'bold'
                  }}
                />
              </Box>
              <LinearProgress
                variant="determinate"
                value={(location.count / maxCount) * 100}
                sx={{
                  height: 6,
                  borderRadius: 3,
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  '& .MuiLinearProgress-bar': {
                    backgroundColor: '#1976d2',
                    borderRadius: 3
                  }
                }}
              />
            </Box>
          </ListItem>
        ))}
      </List>

      <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="body2" sx={{ color: '#bbb' }}>
          Showing top {data.length} locations
        </Typography>
        <Typography variant="body2" sx={{ color: '#bbb' }}>
          Total Clients: {data.reduce((sum, location) => sum + location.count, 0)}
        </Typography>
      </Box>
    </Paper>
  );
};

export { GeographicDistribution };
